/**
 * Schema inference over a batch — the UNION of every item, not item #0.
 *
 * WHY THIS EXISTS: the old Schema lens walked the FIRST row only and said so in
 * a banner ("fields present only on later items aren't surfaced"). That's an
 * honest label on a misleading view: a field that item #0 happens to lack is
 * INVISIBLE, so an optional field — exactly the thing you open a schema to find
 * — is the thing it hides. Worse, the shape it showed was presented as "the"
 * schema of the batch.
 *
 * So: union across items, and record PRESENCE. A field on 3 of 5 items is the
 * single most useful fact a batch schema can tell you, because it's what breaks
 * a downstream expression at item #4.
 *
 * Pure + dependency-free so it's unit-testable and cheap to call per render.
 */

export type SchemaKind = "object" | "array" | "string" | "number" | "boolean" | "null";

export interface SchemaField {
  /** Leaf name (`amount`) or `[]` for an array's element shape. */
  key: string;
  /** Unique within the tree — React keys + future search. */
  path: string;
  /** Every type observed across the sampled items, sorted, deduped. A field
   *  with >1 kind is a REAL signal (a union upstream), not noise. */
  kinds: SchemaKind[];
  /** How many sampled items carry this field, out of `total`. */
  present: number;
  total: number;
  /** First non-undefined value seen — the "example" column. */
  sample: unknown;
  children: SchemaField[];
}

export interface InferredSchema {
  fields: SchemaField[];
  /** Items actually walked (capped). */
  sampled: number;
  /** Items in the batch. `sampled < total` ⇒ the UI must say so. */
  total: number;
}

/** Walking every item of a 100k batch would jank the render; 200 is plenty to
 *  surface an optional field while staying instant. The UI reports the cap. */
const SAMPLE_CAP = 200;

export function schemaKindOf(v: unknown): SchemaKind {
  if (v === null || v === undefined) return "null";
  if (Array.isArray(v)) return "array";
  return typeof v === "object" ? "object" : (typeof v as "string" | "number" | "boolean");
}

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return v !== null && typeof v === "object" && !Array.isArray(v);
}

/** Merge one observed value into the accumulator for `path`. */
function observe(acc: Map<string, SchemaField>, key: string, path: string, value: unknown): void {
  let f = acc.get(path);
  if (!f) {
    f = { key, path, kinds: [], present: 0, total: 0, sample: undefined, children: [] };
    acc.set(path, f);
  }
  f.present += 1;
  const kind = schemaKindOf(value);
  if (!f.kinds.includes(kind)) f.kinds.push(kind);
  if (f.sample === undefined && value !== undefined) f.sample = value;
}

function buildFields(values: unknown[], basePath: string): SchemaField[] {
  const acc = new Map<string, SchemaField>();
  const objects = values.filter(isPlainObject);

  for (const obj of objects) {
    for (const [k, v] of Object.entries(obj)) {
      observe(acc, k, `${basePath}.${k}`, v);
    }
  }
  const fields = [...acc.values()];
  for (const f of fields) {
    // `total` is the number of items that COULD have carried it — the objects
    // we walked. present/total is then the honest presence ratio.
    f.total = objects.length;
    const childValues = objects.map((o) => o[f.key]).filter((v) => v !== undefined);

    if (f.kinds.includes("object")) {
      f.children = buildFields(childValues.filter(isPlainObject), f.path);
    } else if (f.kinds.includes("array")) {
      // Describe the ELEMENT shape: union every element of every occurrence,
      // so an array whose items differ shows a multi-kind `[]` child.
      const elements = childValues.filter(Array.isArray).flat();
      const elemKinds: SchemaKind[] = [];
      for (const e of elements) {
        const k = schemaKindOf(e);
        if (!elemKinds.includes(k)) elemKinds.push(k);
      }
      if (elements.length > 0) {
        f.children = [
          {
            key: "[]",
            path: `${f.path}[]`,
            kinds: elemKinds.sort(),
            present: elements.length,
            total: elements.length,
            sample: elements[0],
            children: buildFields(elements.filter(isPlainObject), `${f.path}[]`),
          },
        ];
      }
    }
    f.kinds.sort();
  }
  return fields;
}

/**
 * The synthetic field a batch of SCALARS gets. An item's data doesn't have to be
 * an object — Split Out emits the bare element, so a batch is routinely
 * `[1, 2, 3]`. Such a batch has no FIELDS, and saying only "these aren't objects"
 * is a dead end: JSON shows the values, so the Schema lens looking empty next to
 * it reads as broken (it isn't — it just had nothing to say). One row naming the
 * value itself is the honest answer, and its path (`$`) is the item, so the
 * picker binds it as `{{ $json }}`.
 */
export const VALUE_FIELD_KEY = "(valore)";

/**
 * Infer the schema of a batch.
 *
 * `rows` are the UNWRAPPED item payloads (the caller has already peeled the
 * `{json: …}` envelope), so this stays a plain "shape of these objects" helper
 * with no knowledge of the Item model.
 */
export function inferSchema(rows: unknown[], cap: number = SAMPLE_CAP): InferredSchema {
  const total = rows.length;
  const sample = rows.slice(0, cap);
  const fields = buildFields(sample, "$");

  // No object in the batch at all ⇒ the items ARE the values. Describe them
  // rather than showing nothing. Guarded on `isPlainObject` and not just on
  // "no fields": a batch of EMPTY objects has no fields either, but its items
  // are objects — calling them "(valore)" would be a lie. A MIXED batch keeps
  // the object fields (the scalars are already out of their presence ratio).
  if (fields.length === 0 && sample.length > 0 && !sample.some(isPlainObject)) {
    const kinds: SchemaKind[] = [];
    for (const v of sample) {
      const k = schemaKindOf(v);
      if (!kinds.includes(k)) kinds.push(k);
    }
    return {
      fields: [
        {
          key: VALUE_FIELD_KEY,
          path: "$",
          kinds: kinds.sort(),
          present: sample.length,
          total: sample.length,
          sample: sample.find((v) => v !== undefined),
          children: [],
        },
      ],
      sampled: sample.length,
      total,
    };
  }
  return { fields, sampled: sample.length, total };
}

/** True when a field isn't on every sampled item — the thing that breaks a
 *  downstream expression, so the UI flags it. */
export function isOptional(f: SchemaField): boolean {
  return f.total > 0 && f.present < f.total;
}

/**
 * Filter the schema by a search query (the viewer's search box).
 *
 * A field is kept when its KEY matches (case-insensitive substring) — then its
 * WHOLE subtree stays (you searched for the container, you want its contents)
 * — or when any DESCENDANT matches, in which case it survives as the ancestor
 * path with only the matching children (a match must stay reachable in the
 * tree, not float context-free). A blank query is the identity.
 */
export function filterSchemaFields(fields: SchemaField[], query: string): SchemaField[] {
  const q = query.trim().toLowerCase();
  if (!q) return fields;
  const out: SchemaField[] = [];
  for (const f of fields) {
    if (f.key.toLowerCase().includes(q)) {
      out.push(f);
      continue;
    }
    const children = filterSchemaFields(f.children, q);
    if (children.length > 0) out.push({ ...f, children });
  }
  return out;
}
